// pending-0.1.19 bump for WorkflowEngine port
/**
 * WorkflowEngine (v3) — entity-type compliance rule engine.
 *
 * Ported from SPFx `webparts/accountOpeningV2/services/WorkflowEngine.ts`
 * (WorkflowEngine + parseEntityRule + ComplianceGate/DocumentRequirement
 * structures) and `services/ProceduralGates.ts` (ProceduralGatesEngine —
 * required field, entity-restriction, authorization, relationship, and
 * document-collection gate categories).
 *
 * Shape changes vs SPFx:
 *   - SPFx `Cart` had `people[]`, `products[]`, `ownership[]` in memory.  The
 *     v3 `CartDto` ships `peopleJson` / `productsJson` / `metadataJson` as
 *     JSON strings with a single cart-level `metadataJson.ownership.entityType`
 *     — this engine consumes a parsed, normalized `CartSnapshot` shape
 *     (see `types.ts` below) produced by `useWorkflowGates`.
 *   - Per-entity rule files in `./rules/*` keep the gate catalogue navigable;
 *     SPFx inlined all customer-type branches in ProceduralGates._validate*().
 *
 * NO REACT IMPORTS — this module is pure TypeScript and runs in both browser
 * bundle and (eventually) Azure Function.
 *
 * Consumers:
 *   - `modules/RAO/hooks/useWorkflowGates.ts` — React binding
 *   - `modules/RAO/components/compliance/GateReport.tsx` — UI surface
 *   - `modules/RAO/steps/ReviewStep.tsx` — commit gating
 */

import type { CustomerType } from '@retail-ops/shared-types/enums';
import type { EntityRule, EntityRuleComplianceGate } from '@retail-ops/shared-types/config';
import type { ResolvedGateBadge, ReviewSubtypeGates } from '@retail-ops/shared-types';
import {
  GATE_LABELS,
  type CartSnapshot,
  type GateCategory,
  type GateReport,
  type GateResult,
  type GateStatus,
  type PersonSnapshot,
  type ProductSnapshot,
  type RemediationAction,
} from './workflow-engine/types';
import { runIndividualGates } from './rules/IndividualGates';
import { runJointIndividualGates } from './rules/JointIndividualGates';
import { runBusinessGates } from './rules/BusinessGates';
import { runTrustGates } from './rules/TrustGates';
import { runEstateGates } from './rules/EstateGates';
import { runFiduciaryGates } from './rules/FiduciaryGates';
import { runOrganizationGates } from './rules/OrganizationGates';
import { runMinorGates } from './rules/MinorGates';

// ---------------------------------------------------------------------------
// Entity-type family map — groups CustomerType into the gate profile files.
// Mirrors the entity family split used by SPFx ProceduralGates per-customer-
// type switches (see legacy _validateRequiredFields + _validateRelationships).
// ---------------------------------------------------------------------------

export type EntityFamily =
  | 'individual'
  | 'joint-individual'
  | 'business'
  | 'trust'
  | 'estate'
  | 'fiduciary'
  | 'organization'
  | 'minor';

const ENTITY_FAMILY_MAP: Record<string, EntityFamily> = {
  // Individual family
  Individual: 'individual',
  // Minor family
  UGMA: 'minor',
  UTMA: 'minor',
  MinorAccount: 'minor',
  // Business family
  Corporation: 'business',
  LLC: 'business',
  LLP: 'business',
  LLLP: 'business',
  DBA: 'business',
  SoleProp: 'business',
  Partnership: 'business',
  LimitedPartnership: 'business',
  GeneralPartnership: 'business',
  // Trust family
  Trust: 'trust',
  // Estate family
  Estate: 'estate',
  // Fiduciary family — Conservatorship, RepPayee, VA, etc. land here.
  Conservatorship: 'fiduciary',
  // Organization family — tax-exempt, public-funds, association, etc.
  PublicFund: 'organization',
  NonProfit: 'organization',
  Church: 'organization',
  Association: 'organization',
  Government: 'organization',
  SchoolDistrict: 'organization',
  // Fallbacks
  IRA: 'individual',
  Other: 'individual',
};

export function resolveEntityFamily(type: CustomerType | string | undefined): EntityFamily {
  if (!type) return 'individual';
  return ENTITY_FAMILY_MAP[type] ?? 'individual';
}

// ---------------------------------------------------------------------------
// Runtime-gate-code → backend GateKey alias map
// ---------------------------------------------------------------------------
// The rule files emit SCREAMING_SNAKE_CASE gate codes (RISK_MGMT_REVIEW, BO,
// CIP, OFAC, ...). The admin EntityRuleGateEnforcement table keys on the
// canonical GateKey set seeded in apps/PCBJxChangeApi/Data/Seeds/81-gate-keys.sql
// using PascalCase (RiskPreReview, BOForm, CIP, OFAC, ...). Most match
// case-insensitively; the divergent ones are mapped explicitly so admin
// strategies actually reach the runtime engine.
//
// Bill 2026-05-12 — feedback_admin_configurable_with_legacy_default_20260511 +
// feedback_admin_configurable_gates_with_deferred_option_20260511 require this
// pipe-through; without it the admin EntityRuleGateEnforcement editor is
// decorative (the strategy column has no runtime effect).
const RUNTIME_TO_BACKEND_GATE_KEY: Record<string, string> = {
  RISK_MGMT_REVIEW: 'RiskPreReview',
  COURT_ORDER_REVIEW: 'RiskPreReview',
  BO: 'BOForm',
  BO_CONTROLLER_ONLY: 'BOForm',
};

function resolveBackendGateKey(runtimeCode: string): string {
  return RUNTIME_TO_BACKEND_GATE_KEY[runtimeCode] ?? runtimeCode;
}

// ---------------------------------------------------------------------------
// WorkflowEngine
// ---------------------------------------------------------------------------

export interface WorkflowEngineOptions {
  /** Entity rules loaded from the /v3/config endpoint (SQL EntityRules table). */
  rules?: ReadonlyArray<EntityRule>;
  /**
   * Compliance gates (flat array, joined to rules by customerType).
   * P0 fix 2026-04-27 — wire returns this as a sibling of `rules` rather
   * than nested. Caller should pass `config.entityComplianceGates`.
   */
  complianceGates?: ReadonlyArray<EntityRuleComplianceGate>;
  /** Overrides for `canCommit` — allow config-driven softening of specific codes. */
  softOverrideCodes?: ReadonlyArray<string>;
  /**
   * Per-(customerType × gateKey) admin enforcement strategies sourced from
   * the live /v3/carts/{id}/review-summary response. When present, the engine
   * post-processes results: `warn-only` downgrades fail/blocked → warn;
   * `deferred-N-days-post-acctadd` downgrades fail/blocked → pending (the
   * DeferredGateTracking row is auto-created by CartCommitService at AcctAdd
   * time — see BACKLOG-0018 Gap 4); `officer-override` downgrades fail/blocked
   * → warn (no ledger row is written for this strategy; the admin's choice of
   * strategy is itself the softening). `required-hard-stop` is the default and
   * applies no transform.
   *
   * Bill 2026-05-12 — required by both admin-configurable doctrine memos:
   * feedback_admin_configurable_with_legacy_default_20260511 + the deferred
   * option variant (..._20260511). Without this pipe-through the
   * EntityRuleGateEnforcement editor was decorative.
   */
  subtypeGates?: ReadonlyArray<ReviewSubtypeGates>;
}

export class WorkflowEngine {
  private readonly rulesByType: Record<string, EntityRule> = {};
  private readonly gatesByType: Record<string, EntityRuleComplianceGate[]> = {};
  private readonly softCodes: Set<string>;
  /** Keyed `${customerType}:${backendGateKey}` → resolved strategy badge. */
  private readonly strategyByKey: Map<string, ResolvedGateBadge> = new Map();

  constructor(options: WorkflowEngineOptions = {}) {
    const rules = options.rules ?? [];
    for (const rule of rules) {
      if (rule.isActive) this.rulesByType[rule.customerType] = rule;
    }
    for (const cg of options.complianceGates ?? []) {
      if (!cg.customerType) continue;
      (this.gatesByType[cg.customerType] ??= []).push(cg);
    }
    this.softCodes = new Set(options.softOverrideCodes ?? []);

    for (const subtype of options.subtypeGates ?? []) {
      for (const badge of subtype.gates) {
        this.strategyByKey.set(`${subtype.customerType}:${badge.gateKey}`, badge);
      }
    }
  }

  /** Lookup EntityRule (config-backed) for a CustomerType. */
  getRule(entityType: CustomerType | string): EntityRule | undefined {
    return this.rulesByType[entityType];
  }

  /**
   * Evaluate every gate applicable to `snapshot.entityType` and return a
   * consolidated GateReport.  Dispatches to per-family rule files; also
   * folds in the entity-rule-driven gates (complianceGates + requiredDocs)
   * when a matching EntityRule was loaded into the engine.
   */
  evaluate(snapshot: CartSnapshot): GateReport {
    const family = resolveEntityFamily(snapshot.entityType);
    const results: GateResult[] = [];

    // Universal preflight — cart must have 1+ person and 1+ product.
    if (snapshot.people.length === 0) {
      results.push({
        gateCode: 'CART_NO_PEOPLE',
        entityType: snapshot.entityType ?? '',
        category: 'structure',
        status: 'blocked',
        label: 'At least one applicant required',
        reason: 'No people have been added to the cart.',
        remediationAction: {
          label: 'Add applicant',
          deepLinkStepId: 'people',
        },
      });
    }
    if (snapshot.products.length === 0) {
      results.push({
        gateCode: 'CART_NO_PRODUCTS',
        entityType: snapshot.entityType ?? '',
        category: 'structure',
        status: 'blocked',
        label: 'At least one product required',
        reason: 'No products have been added to the cart.',
        remediationAction: {
          label: 'Add product',
          deepLinkStepId: 'products',
        },
      });
    }

    // Family dispatch — one file per family, each returns GateResult[].
    switch (family) {
      case 'individual':
        results.push(...runIndividualGates(snapshot));
        if (snapshot.isJoint) results.push(...runJointIndividualGates(snapshot));
        break;
      case 'joint-individual':
        results.push(...runJointIndividualGates(snapshot));
        break;
      case 'business':
        results.push(...runBusinessGates(snapshot));
        break;
      case 'trust':
        results.push(...runTrustGates(snapshot));
        break;
      case 'estate':
        results.push(...runEstateGates(snapshot));
        break;
      case 'fiduciary':
        results.push(...runFiduciaryGates(snapshot));
        break;
      case 'organization':
        results.push(...runOrganizationGates(snapshot));
        break;
      case 'minor':
        results.push(...runMinorGates(snapshot));
        break;
    }

    // EntityRule-driven gates (complianceGates + requiredDocs) — layered on
    // top of code-driven gates.  Config wins for labels; code wins for status
    // computation (a config gate with no code evaluator is `pending`).
    const rule = this.getRule(snapshot.entityType ?? '');
    const gates = this.gatesByType[snapshot.entityType ?? ''] ?? [];
    if (rule && gates.length > 0) {
      for (const cg of gates) {
        const existing = results.find((r) => r.gateCode === cg.gateCode);
        if (existing) {
          // Use config label if the code path didn't label it.
          if (!existing.label || existing.label === existing.gateCode) {
            existing.label = cg.gateName;
          }
          continue;
        }
        results.push({
          gateCode: cg.gateCode,
          entityType: snapshot.entityType ?? '',
          category: 'compliance',
          status: cg.isMandatory ? 'pending' : 'warn',
          label: cg.gateName,
          reason: cg.notes ?? undefined,
          // TODO(v3.1): deep-link config gates to the step owning the proof.
        });
      }
    }

    // Apply soft-overrides — downgrade selected `fail`/`blocked` to `warn`.
    for (const r of results) {
      if ((r.status === 'fail' || r.status === 'blocked') && this.softCodes.has(r.gateCode)) {
        r.status = 'warn';
        r.reason = (r.reason ?? '') + ' (soft-gated by config — follow-up task will be created).';
      }
    }

    // Admin per-(customerType × gateKey) enforcement strategies — see
    // WorkflowEngineOptions.subtypeGates docblock above.
    this.applySubtypeStrategies(results, snapshot);

    return this.summarize(snapshot.entityType ?? '', results, family);
  }

  /**
   * Translate admin EntityRuleGateEnforcement strategies into status transforms
   * on the in-flight gate results. Runs LAST so strategy overrides take precedence
   * over code-side defaults (admin can always soften, never stiffen).
   */
  private applySubtypeStrategies(results: GateResult[], snapshot: CartSnapshot): void {
    if (this.strategyByKey.size === 0) return;
    const customerType = snapshot.entityType ?? '';
    for (const r of results) {
      const backendKey = resolveBackendGateKey(r.gateCode);
      const badge = this.strategyByKey.get(`${customerType}:${backendKey}`);
      if (!badge) continue;

      // Only fail/blocked gates have softenable status. pass/warn/pending are
      // unaffected by strategies — admin can't make a passing gate fail.
      if (r.status !== 'fail' && r.status !== 'blocked') continue;

      const citation = badge.citationKey ? ` (citation: ${badge.citationKey})` : '';
      const subtypeOverrideNote = badge.isSubtypeOverride ? ' [subtype override]' : ' [default]';

      switch (badge.strategy) {
        case 'warn-only':
          r.status = 'warn';
          r.reason = (r.reason ?? '') +
            ` Admin strategy 'warn-only'${subtypeOverrideNote}${citation}.`;
          break;
        case 'deferred-N-days-post-acctadd':
          r.status = 'pending';
          r.reason = (r.reason ?? '') +
            ` Admin strategy 'deferred-${badge.deferralDays ?? 'N'}-days-post-acctadd'` +
            `${subtypeOverrideNote}${citation}. DeferredGateTracking row is auto-created at AcctAdd.`;
          break;
        case 'officer-override':
          r.status = 'warn';
          r.reason = (r.reason ?? '') +
            ` Admin strategy 'officer-override'${subtypeOverrideNote}${citation}.`;
          break;
        case 'required-hard-stop':
        default:
          // No transform — fail/blocked remains. This is the canonical default.
          break;
      }
    }
  }

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  private summarize(entityType: string, gates: GateResult[], family: EntityFamily): GateReport {
    let blockerCount = 0;
    let warnCount = 0;
    let passCount = 0;
    for (const g of gates) {
      if (g.status === 'blocked' || g.status === 'fail') blockerCount += 1;
      else if (g.status === 'warn') warnCount += 1;
      else if (g.status === 'pass') passCount += 1;
    }
    return {
      entityType,
      family,
      gates,
      canCommit: blockerCount === 0,
      blockerCount,
      warnCount,
      passCount,
    };
  }

  /** Convenience: resolve a human label for a gate code. */
  static getGateLabel(code: string): string {
    return GATE_LABELS[code] ?? code;
  }
}

// Re-export the shared types so consumers can `import { WorkflowEngine, ...types }` from one path.
export type {
  CartSnapshot,
  PersonSnapshot,
  ProductSnapshot,
  GateResult,
  GateReport,
  GateStatus,
  GateCategory,
  RemediationAction,
};
export { GATE_LABELS };
